// GlobalSearchModal — búsqueda global del header (Ctrl+K).
// Tres secciones: entregas, listados, clientes. Debounce 250ms al backend.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Modal, Input, Spin, Empty } from 'antd';
import {
    FileOutlined,
    UnorderedListOutlined,
    UserOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { searchService } from '../services/api';

const SECTIONS = [
    { key: 'deliveries',   label: 'Entregas',  icon: <FileOutlined /> },
    { key: 'packingLists', label: 'Listados',  icon: <UnorderedListOutlined /> },
    { key: 'clients',      label: 'Clientes',  icon: <UserOutlined /> },
];

const GlobalSearchModal = ({ open, onClose }) => {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState({ deliveries: [], packingLists: [], clients: [] });
    const [loading, setLoading] = useState(false);

    // Reset al cerrar
    useEffect(() => {
        if (!open) {
            setQuery('');
            setResults({ deliveries: [], packingLists: [], clients: [] });
            setLoading(false);
        } else {
            // autofocus tras render
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    // Debounce + fetch
    useEffect(() => {
        if (!open) return undefined;
        const trimmed = query.trim();
        if (trimmed.length < 2) {
            setResults({ deliveries: [], packingLists: [], clients: [] });
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        const handle = setTimeout(async () => {
            try {
                const resp = await searchService.globalSearch(trimmed);
                if (resp.data?.success) {
                    setResults(resp.data.data || { deliveries: [], packingLists: [], clients: [] });
                }
            } catch (err) {
                console.error('Error en búsqueda global:', err);
                setResults({ deliveries: [], packingLists: [], clients: [] });
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(handle);
    }, [query, open]);

    const totalResults = useMemo(
        () => results.deliveries.length + results.packingLists.length + results.clients.length,
        [results]
    );

    const goto = (path) => {
        onClose();
        navigate(path);
    };

    const renderRow = ({ key, title, sub, onClick, icon }) => (
        <div
            key={key}
            className="gsm-row"
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
        >
            <span className="gsm-row-icon">{icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div className="gsm-row-title">{title}</div>
                <div className="gsm-row-sub">{sub}</div>
            </div>
        </div>
    );

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            closable={false}
            width={640}
            destroyOnClose
            styles={{ body: { padding: 0 } }}
            centered
        >
            <div className="gsm-search-bar">
                <SearchOutlined className="gsm-search-icon" />
                <Input
                    ref={inputRef}
                    variant="borderless"
                    size="large"
                    placeholder="Buscar entregas, listados o clientes…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoFocus
                />
                {loading && <Spin size="small" style={{ marginRight: 8 }} />}
                <span className="gsm-kbd">Esc</span>
            </div>

            <div className="gsm-results">
                {query.trim().length < 2 && (
                    <div className="gsm-hint">
                        Escribe al menos 2 caracteres. Puedes buscar por número de entrega,
                        folio (LP-…) o nombre de cliente.
                    </div>
                )}

                {query.trim().length >= 2 && !loading && totalResults === 0 && (
                    <div style={{ padding: 24 }}>
                        <Empty description="Sin resultados" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                )}

                {SECTIONS.map(section => {
                    const items = results[section.key] || [];
                    if (items.length === 0) return null;

                    return (
                        <div key={section.key} className="gsm-section">
                            <div className="gsm-section-head">
                                {section.icon} {section.label} <span className="gsm-section-count">({items.length})</span>
                            </div>
                            {section.key === 'deliveries' && items.map(d => renderRow({
                                key: `d-${d.docEntry}`,
                                icon: <FileOutlined />,
                                title: `Entrega ${d.docNum} · ${d.cardName || d.cardCode}`,
                                sub: `${d.docDate ? new Date(d.docDate).toLocaleDateString('es-MX') : ''} · ${d.status === 'bost_Open' ? 'Abierta' : 'Cerrada'}`,
                                onClick: () => goto(`/packing-list/${d.docEntry}`),
                            }))}
                            {section.key === 'packingLists' && items.map(l => renderRow({
                                key: `l-${l.id}`,
                                icon: <UnorderedListOutlined />,
                                title: `${l.packingListNumber || '(sin folio)'} · ${l.cardName || l.cardCode || 'N/A'}`,
                                sub: l.deletionStatus === 'pendiente_eliminacion'
                                    ? 'Solicitud de eliminación'
                                    : (l.status || 'activo'),
                                onClick: () => goto(`/packing-list/${l.docEntry}`),
                            }))}
                            {section.key === 'clients' && items.map(c => renderRow({
                                key: `c-${c.cardCode}`,
                                icon: <UserOutlined />,
                                title: c.cardName,
                                sub: `${c.cardCode}${c.country ? ' · ' + c.country : ''}`,
                                onClick: () => goto(`/deliveries?cardName=${encodeURIComponent(c.cardName)}`),
                            }))}
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};

export default GlobalSearchModal;
